import { formatDayKey } from '../../../function-utils/building-time';

/**
 * ONE MONTH OF BUILDING DAY KEYS — pure arithmetic, never a JS local Date.
 *
 * Every value here is a 'YYYY-MM-DD' day key (or a 'YYYY-MM' month key) on the
 * building's calendar. A key is already a calendar day; turning it into a Date
 * with the string constructor reads it as UTC midnight and lands on the day
 * before west of Greenwich, and a local Date moves across a clock change. So
 * this module never constructs a Date at all: days are counted from the civil
 * epoch with integer arithmetic (H. Hinnant's days-from-civil and its inverse),
 * the same method the backend's calendar maths uses.
 *
 * Weeks are Monday-first (en-GB). weekdayOf answers 1 = Monday … 7 = Sunday.
 */

export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_KEY = /^(\d{4})-(\d{2})$/;

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

const keyOf = (year: number, month: number, day: number): string =>
  `${pad(year, 4)}-${pad(month)}-${pad(day)}`;

const monthKeyOf = (year: number, month: number): string =>
  `${pad(year, 4)}-${pad(month)}`;

/** [year, month, day] of a day key, or null when it is not one. */
const partsOf = (day: string): [number, number, number] | null => {
  const m = DAY_KEY.exec(day);

  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
};

const monthPartsOf = (month: string): [number, number] | null => {
  const m = MONTH_KEY.exec(month);
  if (!m) return null;
  const mm = Number(m[2]);

  return mm >= 1 && mm <= 12 ? [Number(m[1]), mm] : null;
};

const isLeap = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const lengthOf = (year: number, month: number): number =>
  month === 2 && isLeap(year) ? 29 : MONTH_LENGTHS[month - 1];

/** Days since 1970-01-01 of a civil date (proleptic Gregorian). */
const daysFromCivil = (year: number, month: number, day: number): number => {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;

  return era * 146097 + doe - 719468;
};

/** The civil date of a count of days since 1970-01-01. */
const civilFromDays = (days: number): [number, number, number] => {
  const z = days + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) /
      365
  );
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const month = mp < 10 ? mp + 3 : mp - 9;
  const year = yoe + era * 400 + (month <= 2 ? 1 : 0);

  return [year, month, day];
};

/** The column names, Monday first. `short` is drawn, `long` is spoken. */
export const WEEKDAYS: ReadonlyArray<{ short: string; long: string }> = [
  { short: 'Mon', long: 'Monday' },
  { short: 'Tue', long: 'Tuesday' },
  { short: 'Wed', long: 'Wednesday' },
  { short: 'Thu', long: 'Thursday' },
  { short: 'Fri', long: 'Friday' },
  { short: 'Sat', long: 'Saturday' },
  { short: 'Sun', long: 'Sunday' },
];

/** How many days a 'YYYY-MM' month has. 0 for something that is not a month. */
export const daysInMonth = (month: string): number => {
  const parts = monthPartsOf(month);

  return parts ? lengthOf(parts[0], parts[1]) : 0;
};

/** 1 = Monday … 7 = Sunday. 1970-01-01 was a Thursday. */
export const weekdayOf = (day: string): Weekday => {
  const parts = partsOf(day);
  if (!parts) return 1;
  const days = daysFromCivil(parts[0], parts[1], parts[2]);

  return ((((days + 3) % 7) + 7) % 7 + 1) as Weekday;
};

/** A day key moved by whole calendar days. */
export const shiftDay = (day: string, n: number): string => {
  const parts = partsOf(day);
  if (!parts) return day;
  const [y, m, d] = civilFromDays(daysFromCivil(parts[0], parts[1], parts[2]) + n);

  return keyOf(y, m, d);
};

/** The 'YYYY-MM' a day key is in. */
export const monthOf = (day: string): string => day.slice(0, 7);

/** A month key moved by whole months. */
export const addMonthKey = (month: string, n: number): string => {
  const parts = monthPartsOf(month);
  if (!parts) return month;
  const total = parts[0] * 12 + (parts[1] - 1) + n;
  const year = Math.floor(total / 12);

  return monthKeyOf(year, total - year * 12 + 1);
};

/** Whole months from one month key to another (b − a). */
export const monthsBetween = (a: string, b: string): number => {
  const first = monthPartsOf(a);
  const second = monthPartsOf(b);
  if (!first || !second) return 0;

  return second[0] * 12 + second[1] - (first[0] * 12 + first[1]);
};

/**
 * The same day n months on, clamped to that month's length: PageDown from
 * 31 Jan 2027 is 28 Feb 2027, never 3 Mar.
 */
export const shiftMonthClamped = (day: string, n: number): string => {
  const parts = partsOf(day);
  if (!parts) return day;
  const target = addMonthKey(monthKeyOf(parts[0], parts[1]), n);
  const [ty, tm] = monthPartsOf(target) as [number, number];

  return keyOf(ty, tm, Math.min(parts[2], lengthOf(ty, tm)));
};

/** The Monday of a day's week. */
export const weekStart = (day: string): string => shiftDay(day, 1 - weekdayOf(day));

/** The Sunday of a day's week. */
export const weekEnd = (day: string): string => shiftDay(day, 7 - weekdayOf(day));

/**
 * The month as rows of seven, Monday first. Days outside the month are null,
 * unless `outside` asks for the neighbouring months' days to fill the rows.
 * Only as many rows as the month needs: four to six.
 */
export const monthWeeks = (
  month: string,
  { outside = false }: { outside?: boolean } = {}
): Array<Array<string | null>> => {
  const parts = monthPartsOf(month);
  if (!parts) return [];
  const first = keyOf(parts[0], parts[1], 1);
  const length = lengthOf(parts[0], parts[1]);
  const lead = weekdayOf(first) - 1;
  const rows = Math.ceil((lead + length) / 7);
  const weeks: Array<Array<string | null>> = [];

  for (let r = 0; r < rows; r += 1) {
    const row: Array<string | null> = [];
    for (let c = 0; c < 7; c += 1) {
      const offset = r * 7 + c - lead;
      const inMonth = offset >= 0 && offset < length;
      row.push(inMonth || outside ? shiftDay(first, offset) : null);
    }
    weeks.push(row);
  }

  return weeks;
};

/** "October 2026". */
export const monthTitle = (month: string): string =>
  monthPartsOf(month)
    ? formatDayKey(`${month}-01`, { month: 'long', year: 'numeric' })
    : month;

/** "Wednesday, 14 October 2026" — the full date a day button must speak. */
export const fullDayName = (day: string): string =>
  partsOf(day)
    ? formatDayKey(day, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : day;
